import { Component, ElementRef, Input, Renderer2, OnInit, AfterViewInit, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { ComparisonType, GameObject, MetricDirection, RankingByLogin, TooltipType } from '../game.models';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { GamesService } from 'src/app/gamification/command-center/services/games.service';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'cxone-bullseye-game',
    templateUrl: './cxone-games-bullseye.component.html',
    styleUrls: ['./cxone-games-bullseye.component.scss']
})
export class CxoneBullseyeGameComponent implements OnInit, AfterViewInit {
    @Input() game: GameObject;
    @Input() gameStatus: string;
    @Input() ranking: RankingByLogin;

    @ViewChild('qualifierText') qualifierText: ElementRef<HTMLElement>;
    @ViewChild('metricText', { static: false }) metricText: ElementRef;
    @ViewChild('tooltip', { static: false }) tooltip!: TooltipDirective;
    @ViewChild('tooltipQualifier', { static: false }) tooltipQualifier!: TooltipDirective;
    @ViewChildren('tooltipBullseye') tooltipBullseye!: QueryList<NgbPopover>;

    gs: GamesService;
    ComparisonType = ComparisonType;
    popoverTitle: string = '';
    TooltipType = TooltipType;
    tooltipValidation: boolean = false;


    constructor(
        public cvLocalizationService: CVLocalizationService,
        public el: ElementRef,
        public renderer: Renderer2,
        private gamesService: GamesService
    ) {
        this.gs = this.gamesService;
    }

    async ngOnInit() {
        await this.loadLocalization();
    }

    ngAfterViewInit() {
        this.addHoverEffects();
        this.updateCircleColors();
    }

    ngOnChanges() {
        if (this.game && this.ranking && this.gameStatus !== "Upcoming") {
            this.updateCircleColors();
        } 
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection('Games');
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        return this.cvLocalizationService.getCachedText('Games', key, defaultValue);
    }

    addHoverEffects() {
        const circles = this.el.nativeElement.querySelectorAll('.bullseye');
        circles.forEach(circle => {
            this.renderer.listen(circle, 'mouseover', (event) => {
                event.stopPropagation();
                if (circle.style.backgroundColor !== 'rgb(0, 137, 64)') {
                    this.renderer.setStyle(circle, 'background-color', '#DBE6F0');
                }
            });

            this.renderer.listen(circle, 'mouseout', (event) => {
                event.stopPropagation();
                if (circle.style.backgroundColor !== 'rgb(0, 137, 64)') {
                    this.renderer.setStyle(circle, 'background-color', '#ECF3F8');
                }
            });
        });
    }

    isWinnerBullseye(index: number): boolean {
        if (
            !this.ranking?.gameRewards?.MyRankingObj ||
            !this.game?.BullseyeRewards[index] ||
            this.game === null ||
            this.ranking === null ||
            this.gameStatus === "Upcoming"
        ) {
            return false;
        }
    
        const reward = this.game?.BullseyeRewards[index];
        const score = this.ranking?.gameRewards?.MyRankingObj?.MetricGroupInfos[0]?.MyMetricValue;
        const qualifierScore = this.ranking?.gameRewards?.MyRankingObj?.MetricGroupInfos[1]?.MyMetricValue;
    
        if (score === undefined || score === null) {
            return false;
        }
    
        if (this.game?.Qualifier !== null && !this.gs.isQualified(qualifierScore, this.game, this.ranking)) {
            return false;
        }
    
        const isUpDirection = this.game?.SelectedMetrics[0]?.direction === MetricDirection.Up;
    
        switch (reward.ComparisonType) {
            case ComparisonType.GreaterThan:
                return score > reward.Target.Val1;
            case ComparisonType.LessThan:
                return score < reward.Target.Val1;
            case ComparisonType.Between:
                return isUpDirection
                    ? score > reward.Target.Val1 && score < reward.Target.Val2
                    : score > reward.Target.Val2 && score < reward.Target.Val1;
            default:
                return false;
        }
    }
    
    getTooltipMetric(index: number, total: number): string {
        const reversedIndex = total - 1 - index;
        const reward = this.game?.BullseyeRewards[reversedIndex];
        if (!reward) return '';

        const formattedVal1 = this.gs.formatterMetricData(this.game?.SelectedMetrics[0]?.conversionType, reward.Target.Val1);
        const formattedVal2 = this.gs.formatterMetricData(this.game?.SelectedMetrics[0]?.conversionType, reward.Target.Val2);

        if (reversedIndex === 0) {
            const isUpMetric = this.game?.SelectedMetrics[0]?.direction === MetricDirection.Up;
            const label = isUpMetric ? this.getLocalizationString('Greater_Than', 'Greater Than') : this.getLocalizationString('Less_Than', 'Less than');
            return `${label} ${formattedVal1}`;
        }
        return `${formattedVal1} ${this.getLocalizationString('To', 'to')} ${formattedVal2} ${this.game?.SelectedMetrics[0]?.abbrev}`;
    }

    isQualifiedBetweenLabel(type: 'left' | 'right', showRight = false): string {
        if (
            this.game == null ||
            this.game?.Qualifier == null 
        ) {
            return '';
        }
    
        const { leftNumber, rightNumber } = this.game?.QualifierLevels;
        
        if (leftNumber !== undefined && rightNumber !== undefined) {
            const minNumber = Math.min(leftNumber, rightNumber);
            const maxNumber = Math.max(leftNumber, rightNumber);
            const formattedMinNumber = this.gs.formatterMetricData(this.game?.Qualifier?.conversionType, minNumber);
            const formattedMaxNumber = this.gs.formatterMetricData(this.game?.Qualifier?.conversionType, maxNumber);
    
            if (showRight) {
                return type === 'left' ? `> ${formattedMinNumber}` : `> ${formattedMaxNumber}`;
            }
            return type === 'left' ? `${formattedMinNumber} <` : `< ${formattedMaxNumber}`;
        }
    
        return type === 'left' ? `${this.gs.formatterMetricData(this.game?.Qualifier?.conversionType, leftNumber)} <` : 
            `< ${this.gs.formatterMetricData(this.game?.Qualifier?.conversionType, rightNumber ?? leftNumber)}`;
    }

    updateCircleColors() {
        const circles = this.el.nativeElement.querySelectorAll('.bullseye');
        let highestAchievedRing = -1;
        
        if (this.game && this.ranking && this.gameStatus !== "Upcoming") {
            for (let i = this.game?.BullseyeRewards?.length - 1; i >= 0; i--) {
                if (this.isWinnerBullseye(i)) {
                    highestAchievedRing = i;
                    break;
                }
            }
        }

        circles.forEach((circle, index) => {
            if (this.game?.BullseyeRewards?.length - 1 - index === highestAchievedRing) {
                this.renderer.setStyle(circle, 'background-color', '#008940');
            } else {
                this.renderer.setStyle(circle, 'background-color', '#ECF3F8');
            }
        });
    }

    getRingStyle(index: number, total: number): { width: string, height: string } {
        const maxDiameter = 112;
        const step = maxDiameter / total;
        const size = maxDiameter - index * step;
        return { width: `${size}px`, height: `${size}px` };
    }

    getPopoverTitle(index: number, total: number): string {
        const reversedIndex = total - 1 - index;
        if (reversedIndex === 0) {
            return  this.getLocalizationString("Bullseye","Bullseye");
        }
        switch (reversedIndex) {
            case 1: return this.getLocalizationString("2nd_Ring","2nd Ring");
            case 2: return this.getLocalizationString("3rd_Ring","3rd Ring");
            case 3: return this.getLocalizationString("4th_Ring", "4th Ring");
            case 4: return this.getLocalizationString("5th_Ring", "5th Ring");
            default: return `${reversedIndex + 1} ${this.getLocalizationString("Ring", "Ring")}`;
        }
    }

    checkTextTruncation(tooltip: string, type: TooltipType): string {
        this.tooltipValidation = true;
        switch (type) {
            case TooltipType.Q1:
                if (this.metricText) {
                    return this.metricText.nativeElement.scrollWidth != this.metricText.nativeElement.clientWidth ? tooltip : "";
                }
                break;
            case TooltipType.Q2:
                if (this.qualifierText) {
                    return this.qualifierText.nativeElement.scrollWidth != this.qualifierText.nativeElement.clientWidth ? tooltip : "";
                }
                break;
            default:
                this.tooltipValidation = false;
        }
        return "";
    }

    hideTooltip() {
        if (this.tooltipBullseye) {
            this.tooltipBullseye.forEach(tooltip => tooltip.close());
        }
    }
}
